import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Save, Trash2, AlertCircle, CheckCircle2 } from "lucide-react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTheme } from "../../hooks/useTheme";
import api from "../../api/api";

const emptyDraft = { title: "", note: "" };

// Manages the onboarding-task catalog (dbo.OnboardingTaskDefinitions, migration
// 033) through the admin API. Adding a task backfills it to every existing
// client and new signups pick it up automatically; edits/deletes persist to the
// DB. (Previously this panel only wrote to localStorage and never reached users.)
const AdminOnboardingChecklist = ({ embedded = false }) => {
  const { isDarkMode } = useTheme();
  const [steps, setSteps] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null); // { type: 'success' | 'error', text }
  const [pendingDelete, setPendingDelete] = useState(null); // step id awaiting delete confirmation
  const [deleting, setDeleting] = useState(false);
  const noticeTimer = useRef(null);

  const skBase = isDarkMode ? "#334155" : "#e2e8f0";
  const skHi = isDarkMode ? "#475569" : "#f1f5f9";

  const flash = useCallback((type, text) => {
    setNotice({ type, text });
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 4000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/onboarding-tasks");
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setSteps(
        items
          .filter((it) => it.is_active)
          .map((it) => ({
            id: it.id,
            taskId: it.task_id,
            title: it.name || "",
            note: it.description || "",
            isActive: it.is_active,
            sortOrder: it.sort_order,
            dirty: false,
          }))
      );
    } catch {
      setSteps([]);
      flash("error", "Couldn’t load onboarding tasks.");
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStep = (id, field, value) => {
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value, dirty: true } : s)));
  };

  const addStep = async () => {
    const name = draft.title.trim();
    if (!name || adding) return;
    setAdding(true);
    try {
      await api.post("/admin/onboarding-tasks", { name, description: draft.note.trim() });
      setDraft(emptyDraft);
      await load();
      flash("success", "Step added and rolled out to users.");
    } catch (err) {
      flash("error", err?.response?.data?.error || "Couldn’t add the step.");
    } finally {
      setAdding(false);
    }
  };

  const dirtySteps = steps.filter((s) => s.dirty);

  const saveAll = async () => {
    if (!dirtySteps.length || saving) return;
    if (dirtySteps.some((s) => !s.title.trim())) {
      flash("error", "Every step needs a title.");
      return;
    }
    setSaving(true);
    try {
      await Promise.all(
        dirtySteps.map((s) =>
          api.put(`/admin/onboarding-tasks/${s.id}`, {
            name: s.title.trim(),
            description: (s.note || "").trim(),
            is_active: s.isActive,
            sort_order: s.sortOrder,
          })
        )
      );
      setSteps((prev) => prev.map((s) => ({ ...s, dirty: false })));
      flash("success", "Changes saved.");
    } catch (err) {
      flash("error", err?.response?.data?.error || "Couldn’t save changes.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const id = pendingDelete;
    if (id === null || deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/onboarding-tasks/${id}`);
      setSteps((prev) => prev.filter((s) => s.id !== id));
      flash("success", "Step removed.");
    } catch (err) {
      flash("error", err?.response?.data?.error || "Couldn’t remove the step.");
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  const cardCls = `rounded-2xl border shadow-sm ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`;
  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm ${
    isDarkMode ? "bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500" : "bg-white border-slate-300 text-slate-800 placeholder-slate-400"
  }`;
  const mobileLabel = `md:hidden text-xs font-bold uppercase tracking-wide mb-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`;

  return (
    <div className={`${embedded ? "w-full" : `min-h-screen w-full ${isDarkMode ? "bg-surface text-slate-100" : "bg-surface text-slate-900"}`}`}>
      <div className={`${embedded ? "p-4 space-y-6" : "max-w-6xl mx-auto px-4 py-8 space-y-6"}`}>

        {notice && (
          <div
            role="alert"
            className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium ${
              notice.type === "error"
                ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
                : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
            }`}
          >
            {notice.type === "error" ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
            {notice.text}
          </div>
        )}

        {/* Add New Step */}
        <div className={`${cardCls} p-6`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Add New Step</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              value={draft.title}
              onChange={(e) => setDraft((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Step title"
              className={inputCls}
            />
            <input
              type="text"
              value={draft.note}
              onChange={(e) => setDraft((prev) => ({ ...prev, note: e.target.value }))}
              placeholder="Optional note"
              className={inputCls}
            />
            <button
              type="button"
              onClick={addStep}
              disabled={!draft.title.trim() || adding}
              className="rounded-lg px-3 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              <Plus size={16} /> {adding ? "Adding…" : "Add Step"}
            </button>
          </div>
        </div>

        {/* List */}
        <div className={`${cardCls} overflow-hidden`}>
          <div className={`hidden md:grid grid-cols-12 gap-3 px-4 py-3 border-b text-xs font-semibold uppercase ${isDarkMode ? "border-slate-700 text-slate-400" : "border-slate-200 text-slate-500"}`}>
            <div className="col-span-6">Title</div>
            <div className="col-span-5">Note</div>
            <div className="col-span-1 text-right">Delete</div>
          </div>

          <div className={`divide-y ${isDarkMode ? "divide-slate-700/60" : "divide-slate-200/60"}`}>
            {loading ? (
              <SkeletonTheme baseColor={skBase} highlightColor={skHi}>
                {[0, 1, 2].map((i) => (
                  <div key={i} className="relative flex flex-col gap-3 px-4 pt-10 pb-4 md:grid md:grid-cols-12 md:gap-3 md:items-center md:py-3">
                    <div className="md:col-span-6"><Skeleton height={38} borderRadius={8} /></div>
                    <div className="md:col-span-5"><Skeleton height={38} borderRadius={8} /></div>
                    <div className="absolute top-2 right-2 md:static md:col-span-1 md:justify-self-end">
                      <Skeleton width={28} height={28} borderRadius={8} />
                    </div>
                  </div>
                ))}
              </SkeletonTheme>
            ) : steps.length === 0 ? (
              <div className={`px-4 py-8 text-center text-sm ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                No onboarding tasks yet. Add one above.
              </div>
            ) : (
              steps.map((step) => (
                <div
                  key={step.id}
                  className="relative flex flex-col gap-3 px-4 pt-10 pb-4 md:grid md:grid-cols-12 md:gap-3 md:items-center md:py-3"
                >
                  <div className="md:col-span-6">
                    <span className={mobileLabel}>Title</span>
                    <input type="text" value={step.title || ""} onChange={(e) => updateStep(step.id, "title", e.target.value)} className={inputCls} />
                  </div>
                  <div className="md:col-span-5">
                    <span className={mobileLabel}>Note</span>
                    <input type="text" value={step.note || ""} onChange={(e) => updateStep(step.id, "note", e.target.value)} className={inputCls} />
                  </div>
                  <button
                    type="button"
                    onClick={() => setPendingDelete(step.id)}
                    title="Delete step"
                    className={`absolute top-2 right-2 md:static md:col-span-1 md:justify-self-end p-2 rounded-lg ${
                      isDarkMode ? "text-rose-300 hover:bg-slate-700" : "text-rose-600 hover:bg-rose-50"
                    }`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Save changes */}
          {!loading && steps.length > 0 && (
            <div className={`flex justify-end px-4 py-3 border-t ${isDarkMode ? "border-slate-700" : "border-slate-200"}`}>
              <button
                type="button"
                onClick={saveAll}
                disabled={!dirtySteps.length || saving}
                className="rounded-lg px-4 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                <Save size={16} /> {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation — app-styled modal in place of window.confirm */}
      {pendingDelete !== null && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={() => !deleting && setPendingDelete(null)}
          />
          <div className={`relative w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 ${isDarkMode ? "bg-slate-800 border border-slate-700" : "bg-white"}`}>
            <div className="p-6">
              <h3 className={`text-lg font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Delete this task?</h3>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setPendingDelete(null)}
                  disabled={deleting}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${
                    isDarkMode ? "bg-slate-700 text-slate-200 hover:bg-slate-600" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-rose-600 text-white hover:bg-rose-700 transition-colors disabled:opacity-50"
                >
                  {deleting ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOnboardingChecklist;
